'use client';

import { DropdownMenuProvider } from '@magicschool/spellbook/components/DropdownMenu/DropdownMenuProvider';
import {
  DropdownMenuActionRowActionSlot,
  DropdownMenuActionRowSlot,
  DropdownMenuCheckboxItemSlot,
  DropdownMenuContentSlot,
  DropdownMenuFooterSlot,
  DropdownMenuGroupSlot,
  DropdownMenuHeaderSlot,
  DropdownMenuItemAvatarSlot,
  DropdownMenuItemContentOnlySlot,
  DropdownMenuItemDescriptionSlot,
  DropdownMenuItemLabelSlot,
  DropdownMenuItemLeadingIconSlot,
  DropdownMenuItemSlot,
  DropdownMenuItemTrailingIconSlot,
  DropdownMenuModalItemSlot,
  DropdownMenuRadioGroupSlot,
  DropdownMenuRadioItemSlot,
  DropdownMenuSubContentSlot,
  DropdownMenuSubSlot,
  DropdownMenuSubTriggerSlot,
  DropdownMenuTooltipContentSlot,
  DropdownMenuTooltipSlot,
  DropdownMenuTriggerSlot,
} from '@magicschool/spellbook/components/DropdownMenu/slots';
import { useControllableState } from '@magicschool/spellbook/hooks/useControllableState';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { useDirection } from 'radix-ui/direction';
import * as DropdownMenuPrimitive from 'radix-ui/dropdown-menu';
import { useMemo, useRef, useState } from 'react';

export type DropdownMenuProps = Omit<React.ComponentProps<typeof DropdownMenuPrimitive.Root>, 'dir'> & {
  /** Side of the trigger the menu opens on. Forwarded to the content. */
  side?: React.ComponentProps<typeof DropdownMenuPrimitive.Content>['side'];
  'data-testid'?: string;
};

export const DropdownMenuBase: React.FC<DropdownMenuProps> = ({
  children,
  'data-testid': dataTestId,
  side,
  defaultOpen,
  modal = true,
  open: providedOpen,
  onOpenChange,
  ...props
}) => {
  const [open, setOpen] = useControllableState({ prop: providedOpen, defaultProp: defaultOpen ?? false, onChange: onOpenChange });
  const [hasOpenModal, setHasOpenModal] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const dir = useDirection();

  const dropdownContextValue = useMemo(
    () => ({
      isOpen: open,
      radioGroupValues: {},
      setRadioGroupValue: () => undefined,
      getRadioGroupValue: () => undefined,
      closeDropdownMenu: () => setOpen(false),
      openModal: () => setHasOpenModal(true),
      closeModal: () => setHasOpenModal(false),
      hasOpenModal,
      triggerRef,
    }),
    [open, hasOpenModal, setOpen],
  );

  const { slots } = useSlots(children, {
    trigger: { Component: DropdownMenuTriggerSlot, props: { 'data-testid': dataTestId, dir } },
    content: { Component: DropdownMenuContentSlot, props: { side, dir, ...props } },
    tooltip: { Component: DropdownMenuTooltipSlot, props: { 'data-testid': dataTestId } },
  });

  return (
    <DropdownMenuProvider value={dropdownContextValue}>
      <DropdownMenuPrimitive.Root defaultOpen={defaultOpen} open={open} onOpenChange={setOpen} modal={modal} dir={dir} {...props}>
        {slots.tooltip ?? slots.trigger}
        {slots.content}
      </DropdownMenuPrimitive.Root>
    </DropdownMenuProvider>
  );
};

/**
 * An action menu for triggering commands, navigation, or settings. Not for form field selection.
 *
 * ### When to use
 * - Overflow menus ("more actions" ellipsis buttons)
 * - Context menus for rows/items (edit, delete, duplicate)
 * - Settings menus with radio/checkbox options
 * - User account menus (profile, settings, logout)
 * - Action menus where clicking triggers a command
 *
 * ### When NOT to use
 * - Form field selection: use `Select` instead (proper form semantics)
 * - Searchable selection: use `Autocomplete` or `MultiSelect` instead
 * - Command palettes with search: use `Command` inside a Popover instead
 *
 * See the [Select documentation](?path=/docs/components-select--docs) for a detailed comparison of selection components.
 *
 * ### API
 * - `open` / `onOpenChange`: Controlled open state
 * - `defaultOpen`: Uncontrolled initial state
 * - `side`: Popover placement (`top`, `right`, `bottom`, `left`)
 * - `modal`: Whether to trap focus (default `true`)
 *
 * ### Composition (slots)
 * **Trigger & Content:**
 * - `<DropdownMenu.Trigger>`: Wrap your trigger button. Use `<Button>` or `<IconButton>`.
 * - `<DropdownMenu.Content>`: Menu content container.
 *
 * **Items:**
 * - `<DropdownMenu.Item>`: Action item. Use `onSelect` for click handler.
 * - `<DropdownMenu.ItemLabel>`: Label text (recommended for all items).
 * - `<DropdownMenu.ItemDescription>`: Secondary text below label.
 * - `<DropdownMenu.ItemLeadingIcon>`: Icon before label.
 * - `<DropdownMenu.ItemTrailingIcon>`: Icon after label.
 * - `<DropdownMenu.ItemAvatar>`: Avatar before label.
 *
 * **Rows that are not items:**
 * - `<DropdownMenu.ActionRow>`: Inert row (text, description, icon) whose only menuitem is its `ActionRowAction`.
 * - `<DropdownMenu.ActionRowAction>`: The row's button, registered with Radix as the menuitem. See the "With Action Row" story.
 *
 * **Grouping:**
 * - `<DropdownMenu.Group>`: Groups items with automatic separator.
 * - `<DropdownMenu.Header>`: Group title/label.
 *
 * **Selection items:**
 * - `<DropdownMenu.RadioGroup>`: Single-select group. Props: `id`, `value`, `onValueChange`.
 * - `<DropdownMenu.RadioItem>`: Radio option within a RadioGroup.
 * - `<DropdownMenu.CheckboxItem>`: Checkbox option. Props: `checked`, `onCheckedChange`.
 *
 * **Submenus (Radix-native):**
 * - `<DropdownMenu.Sub>`: Submenu wrapper (wraps SubTrigger + SubContent).
 * - `<DropdownMenu.SubTrigger>`: Item that opens submenu. Auto-adds chevron icon.
 * - `<DropdownMenu.SubContent>`: Submenu content container. Supports infinite nesting.
 *
 * **Modal integration:**
 * - `<DropdownMenu.ModalItem>`: Item that opens a modal. Handles focus automatically.
 *   - Use `preserveDropdownOnModalClose` to keep dropdown open after modal closes.
 *
 * **Tooltip:**
 * - `<DropdownMenu.Tooltip>`: Wrap around trigger for hover tooltip.
 * - `<DropdownMenu.TooltipContent>`: Tooltip text.
 *
 * ### Accessibility
 * - Uses menu/menuitem ARIA roles
 * - Arrow keys navigate items, Enter activates, Escape closes
 * - Focus trapped within menu when open; Tab never moves inside the menu, so only registered items are reachable
 * - `Header` is a label, not a stop; a `Group` that contains one is labelled by it automatically
 * - Radix UI handles all accessibility automatically
 *
 * @headless Radix DropdownMenu root renders no element; target `dropdown-menu-trigger` and `dropdown-menu-content`.
 */
export const DropdownMenu = Object.assign(DropdownMenuBase, {
  Trigger: DropdownMenuTriggerSlot,
  Content: DropdownMenuContentSlot,
  Header: DropdownMenuHeaderSlot,
  Footer: DropdownMenuFooterSlot,
  Group: DropdownMenuGroupSlot,
  Item: DropdownMenuItemSlot,
  ActionRow: DropdownMenuActionRowSlot,
  ActionRowAction: DropdownMenuActionRowActionSlot,
  ItemContentOnly: DropdownMenuItemContentOnlySlot,
  ItemLeadingIcon: DropdownMenuItemLeadingIconSlot,
  ItemTrailingIcon: DropdownMenuItemTrailingIconSlot,
  ItemLabel: DropdownMenuItemLabelSlot,
  ItemDescription: DropdownMenuItemDescriptionSlot,
  ItemAvatar: DropdownMenuItemAvatarSlot,
  Sub: DropdownMenuSubSlot,
  SubTrigger: DropdownMenuSubTriggerSlot,
  SubContent: DropdownMenuSubContentSlot,
  RadioGroup: DropdownMenuRadioGroupSlot,
  RadioItem: DropdownMenuRadioItemSlot,
  CheckboxItem: DropdownMenuCheckboxItemSlot,
  ModalItem: DropdownMenuModalItemSlot,
  Tooltip: DropdownMenuTooltipSlot,
  TooltipContent: DropdownMenuTooltipContentSlot,
});
